import React, {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import createJustifiedLayout from "justified-layout";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useClient } from "../../../hooks/useClient";
import { useLogicalSelectedPage, useSelectedZone } from "../../../hooks/useSelection";
import { useSymbols } from "../../../hooks/useSymbols";
import { useResizeObserver } from "../../../hooks/useResizeObserver";
import { useSelectedSymbol } from "../../bottom-menu-panels/symbols/symbols-list/use-selected-symbol";
import { PlaceholderImage } from "../../placeholder";
import { ISidebarContentRef, SidebarPanel } from "../sidebar-panel";
import { scrollIntoView } from "../../../utils/dom";

import "./sidebar-symbols-panel.css";
import { useDefinitionFeatures, useSpreadFeatures } from "../../../hooks/useDefinitionFeatures";

const SidebarSymbolsItem = ({
	thumbUrl,
	isActive,
	isWhite,
	onClick,
}: {
	thumbUrl: string;
	isActive?: boolean;
	isWhite?: boolean;
	onClick?(): void;
}) => (
	<button
		className="sidebar-symbols-item"
		aria-selected={isActive}
		{...{ "data-checked": isActive || undefined }}
		role="option"
		onClick={(e) => {
			e.stopPropagation();
			if (onClick) onClick();
		}}
	>
		<div
			className={`sidebar-symbols-item__img-container${
				isWhite ? " sidebar-symbols-item__img-container--gray" : ""
			}`}
		>
			<PlaceholderImage className="sidebar-symbols-item__img" src={thumbUrl} loading="lazy" />
		</div>
	</button>
);

export const SidebarSymbolsPanel = memo(() => {
	const client = useClient();
	const dashboard = useDashboardContext();
	const selectedZone = useSelectedZone();
	const { data: symbols } = useSymbols();
	const currentSymbol = useSelectedSymbol(selectedZone);
	const panelRef = useRef<ISidebarContentRef | null>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(0);

	const { areSymbolsSupported } = useDefinitionFeatures();
	const { hasFillableSymbolZonesInSpread } = useSpreadFeatures();
	const selectedPage = useLogicalSelectedPage();
	const areSymbolsDisabled = !(
		hasFillableSymbolZonesInSpread ||
		(selectedPage && dashboard.canAddSymbolZone(selectedPage))
	);

	// Measure initial width for grid
	useLayoutEffect(() => {
		if (gridRef.current) {
			setContainerWidth(gridRef.current.getBoundingClientRect().width ?? 0);
		}
	}, []);

	// Watch width changes for grid
	useResizeObserver(gridRef, () => {
		setContainerWidth(gridRef.current?.getBoundingClientRect().width ?? 0);
	});

	const visibleSymbols = useMemo(
		() =>
			symbols?.flatMap((s) => {
				const thumbUrl = s.thumbUrl?.getAbsoluteUrl(client);
				return thumbUrl ? [{ code: s.code, thumbUrl, isWhite: s.isWhite }] : [];
			}) ?? [],
		[client, symbols],
	);

	const showGrid = containerWidth > 0;

	const { boxes, containerHeight } = useMemo(() => {
		if (!visibleSymbols.length || !containerWidth) {
			return { boxes: null, containerHeight: 0 };
		}

		const boxSpacing = 8;
		const sidebarPanelPadding = 12;
		return createJustifiedLayout(
			visibleSymbols.map(() => 1),
			{
				containerPadding: sidebarPanelPadding,
				containerWidth,
				targetRowHeight: 80,
				boxSpacing,
				forceAspectRatio: 1,
			},
		);
	}, [containerWidth, visibleSymbols]);

	// Scroll to active symbol
	useEffect(() => {
		const index = visibleSymbols.findIndex((s) => s.code === currentSymbol?.code);
		const el = gridRef.current?.children.item(index);
		if (el) {
			scrollIntoView(el, {
				behavior: "instant",
				container: panelRef.current?.scrollableEl,
				margins: eXpress.math.margins(0, 50),
			});
		}
		// boxes in deps so we wait until elements are rendered
	}, [currentSymbol, visibleSymbols, boxes]);

	const onSymbolClick = useCallback(
		(code: string) => {
			const newSymbol = symbols?.find((sym) => sym.code === code);
			if (!newSymbol) return;
			if (newSymbol === currentSymbol) return;

			if (dashboard.canReplaceSymbol(selectedZone, false)) {
				dashboard.replaceSymbol(selectedZone, newSymbol, false);
			} else if (dashboard.canAddSymbolZone(selectedZone?.page)) {
				dashboard.addSymbolZone({
					symbol: newSymbol,
					page: selectedZone?.page,
				});
			}
		},
		[currentSymbol, dashboard, selectedZone, symbols],
	);

	if (!areSymbolsSupported) return null;
	if (areSymbolsDisabled) return null;

	return (
		<SidebarPanel.Root>
			<SidebarPanel.Content
				ref={panelRef}
				className="sidebar-symbols-panel"
				scrollable
				fade
				noPadding
			>
				<div
					ref={gridRef}
					className="sidebar-symbols-panel__grid"
					role="listbox"
					style={{ height: containerHeight }}
				>
					{showGrid &&
						boxes?.map((box, index) => {
							const symbol = visibleSymbols[index];
							return (
								<div
									key={symbol.code}
									style={{
										position: "absolute",
										top: box.top,
										left: box.left,
										width: box.width,
										height: box.height,
									}}
								>
									<SidebarSymbolsItem
										thumbUrl={symbol.thumbUrl}
										isActive={currentSymbol?.code === symbol.code}
										isWhite={symbol.isWhite}
										onClick={() => onSymbolClick(symbol.code)}
									/>
								</div>
							);
						})}
				</div>
			</SidebarPanel.Content>
		</SidebarPanel.Root>
	);
});
SidebarSymbolsPanel.displayName = "SidebarSymbolsPanel";
